import React, { createContext, useContext, useState } from 'react';

const AdminContext = createContext();

export function useAdmin() {
  return useContext(AdminContext);
}

export function AdminProvider({ children }) {
  const [pendingMembers, setPendingMembers] = useState([
    {
      id: 1,
      firstName: 'John',
      lastName: 'Smith',
      email: 'john.smith@example.com',
      membershipType: 'individual',
      submittedAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
      status: 'pending',
    },
    {
      id: 2,
      firstName: 'Mary',
      lastName: 'Johnson',
      email: 'mary.johnson@example.com',
      membershipType: 'family',
      submittedAt: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
      status: 'pending',
    },
    {
      id: 3,
      firstName: 'David',
      lastName: 'Williams',
      email: 'david.williams@example.com',
      membershipType: 'individual',
      submittedAt: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
      status: 'pending',
    },
    {
      id: 4,
      firstName: 'Sarah',
      lastName: 'Brown',
      email: 'sarah.brown@example.com',
      membershipType: 'youth',
      submittedAt: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
      status: 'pending',
    },
    {
      id: 5,
      firstName: 'Michael',
      lastName: 'Davis',
      email: 'michael.davis@example.com',
      membershipType: 'individual',
      submittedAt: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
      status: 'pending',
    },
    {
      id: 6,
      firstName: 'Emma',
      lastName: 'Wilson',
      email: 'emma.wilson@example.com',
      membershipType: 'family',
      submittedAt: new Date(Date.now() - 7 * 60 * 60 * 1000).toISOString(),
      status: 'pending',
    },
    {
      id: 7,
      firstName: 'James',
      lastName: 'Taylor',
      email: 'james.taylor@example.com',
      membershipType: 'individual',
      submittedAt: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
      status: 'pending',
    },
    {
      id: 8,
      firstName: 'Sophie',
      lastName: 'Anderson',
      email: 'sophie.anderson@example.com',
      membershipType: 'youth',
      submittedAt: new Date(Date.now() - 9 * 60 * 60 * 1000).toISOString(),
      status: 'pending',
    },
    {
      id: 9,
      firstName: 'Oliver',
      lastName: 'Thomas',
      email: 'oliver.thomas@example.com',
      membershipType: 'individual',
      submittedAt: new Date(Date.now() - 10 * 60 * 60 * 1000).toISOString(),
      status: 'pending',
    },
    {
      id: 10,
      firstName: 'Emily',
      lastName: 'Martin',
      email: 'emily.martin@example.com',
      membershipType: 'family',
      submittedAt: new Date(Date.now() - 11 * 60 * 60 * 1000).toISOString(),
      status: 'pending',
    },
    {
      id: 11,
      firstName: 'Daniel',
      lastName: 'White',
      email: 'daniel.white@example.com',
      membershipType: 'individual',
      submittedAt: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
      status: 'pending',
    },
    {
      id: 12,
      firstName: 'Lucy',
      lastName: 'Clark',
      email: 'lucy.clark@example.com',
      membershipType: 'youth',
      submittedAt: new Date(Date.now() - 13 * 60 * 60 * 1000).toISOString(),
      status: 'pending',
    },
  ]);

  const [recentActivity, setRecentActivity] = useState([
    {
      id: 1,
      type: 'approval',
      memberName: 'Sarah Thompson',
      timestamp: new Date(Date.now() - 10 * 60 * 1000).toISOString(),
    },
    {
      id: 2,
      type: 'payment_failed',
      memberName: 'Michael Brown',
      timestamp: new Date(Date.now() - 25 * 60 * 1000).toISOString(),
    },
    {
      id: 3,
      type: 'details_updated',
      memberName: 'James Wilson',
      timestamp: new Date(Date.now() - 60 * 60 * 1000).toISOString(),
    },
  ]);

  const approveMember = async memberId => {
    try {
      // Check if member exists
      const member = pendingMembers.find(m => m.id === memberId);
      if (!member) {
        throw new Error('Member not found');
      }

      // Update pending members
      setPendingMembers(prev => prev.filter(m => m.id !== memberId));

      // Add to recent activity
      setRecentActivity(prev => [
        {
          id: Date.now(),
          type: 'approval',
          memberName: `${member.firstName} ${member.lastName}`,
          timestamp: new Date().toISOString(),
        },
        ...prev,
      ]);

      return true;
    } catch (error) {
      console.error('Failed to approve member:', error);
      return false;
    }
  };

  const rejectMember = async (memberId, reason) => {
    try {
      // Check if member exists
      const member = pendingMembers.find(m => m.id === memberId);
      if (!member) {
        throw new Error('Member not found');
      }

      // Update pending members
      setPendingMembers(prev => prev.filter(m => m.id !== memberId));

      // Add to recent activity
      setRecentActivity(prev => [
        {
          id: Date.now(),
          type: 'rejection',
          memberName: `${member.firstName} ${member.lastName}`,
          reason,
          timestamp: new Date().toISOString(),
        },
        ...prev,
      ]);

      return true;
    } catch (error) {
      console.error('Failed to reject member:', error);
      return false;
    }
  };

  const value = {
    pendingMembers,
    recentActivity,
    approveMember,
    rejectMember,
  };

  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>;
}
